import React from 'react';
import OfferSkeleton from 'pages/offer/offer-skeleton';
import styles from './styles.module.scss';
import DeviceTypeIcon from '../device-type-icon';
import {IOfferWithRewards, IRewards} from 'types/offers';
import classNames from 'classnames';
import {Rating} from '../chakra-ui/rating';
import Cta from '../cta';
import NoData from '../no-data';
import {getOS} from '../../helpers';
import {QrCode} from "@chakra-ui/react"
import {useQuery} from 'react-query';
import {QUERY_KEYS} from '../../constants';
import {rewardsHistory} from 'api/user';

const ItemDetails: React.FC<{
  data: IOfferWithRewards | null;
  loading: boolean;
  showButton: boolean;
}> = ({data, loading, showButton}) => {

  // Fetch completed rewards to show visual feedback
  const { data: completedRewards = [] } = useQuery(
    [QUERY_KEYS.USER.REWARDS_HISTORY],
    rewardsHistory, {
    refetchInterval: 1000 * 10, // Auto-refresh every 10 secs
    refetchIntervalInBackground: false,
    staleTime: 1000 * 10,
    refetchOnWindowFocus: true,
    refetchOnMount: true,
    keepPreviousData: true,
    retry: (failureCount, error: any) => {
      const status = error?.status;
      return failureCount < 1 && ![400, 401, 404].includes(status);
    },
    retryDelay: () => 3000, // 3s delay before the single retry
  });

  // Create a Map for completed rewards with timestamps for quick lookup (O(1) complexity)
  const completedRewardsMap = React.useMemo(() => {
    const map = new Map();
    completedRewards.forEach((completed: any) => {
      const key = `${completed.offer_id}-${completed.event_id}`;
      // Try different possible timestamp field names
      const timestamp = completed.event_time;
      map.set(key, timestamp);
    });
    return map;
  }, [completedRewards]);

  // Helper function to check if a reward is completed
  const isRewardCompleted = React.useCallback((rewardId: string, offerId: string) => {
    const key = `${offerId}-${rewardId}`;
    return completedRewardsMap.has(key); // Map.has() is O(1) just like Set.has()
  }, [completedRewardsMap]);

  // Helper function to check if a reward was completed recently (within 10 minutes)
  const isRewardRecentlyCompleted = React.useCallback((rewardId: string, offerId: string) => {
    const key = `${offerId}-${rewardId}`;
    const timestamp = completedRewardsMap.get(key);
    
    if (!timestamp) return false;
    
    // Convert timestamp to Date object
    const completedTime = new Date(timestamp); // Here tz info must be provided in the timestamp for correct calculation
    const now = new Date();  // Current time in local timezone (including tz info)
    // Calculate difference in minutes
    // Note: getTime() always returns milliseconds since UTC epoch, so comparison is safe, even for different tz, because both dates get converted to UTC milliseconds, just make sure tz info is included in the timestamp 
    const diffInMinutes = (now.getTime() - completedTime.getTime()) / (1000 * 60);
    
    return diffInMinutes <= 10;
  }, [completedRewardsMap]);

  // Generate QR code component - set during render, save to var and rerun only if data changes
  const qrCodeComponent = React.useMemo(() => {
    // If no data or no offer id or no game link, return null
    if (!data || !data.offer_id || !data.game_link) return null;

    // If device is iOS or Android, do not generate QR code
    if (['iOS', 'Android'].includes(getOS())) return null;

    // Generate QR code component with chakra-ui for desktop users
    if (getOS() === 'Other') {
      return (
        <QrCode.Root size={"xl"} value={data.game_link} key={data.offer_id}>
          <QrCode.Frame>
            <QrCode.Pattern />
          </QrCode.Frame>
        </QrCode.Root>
      );
    }

    return null;
  }, [data]);

  // Return placeholder if there's no data
  if (!loading && !data) {
    return <NoData />;
  }

  // Return loading skeleton while fetching
  if (loading) {
    return <OfferSkeleton />;
  }

  // Main UI once data is ready
  return (
    <div className={styles.item}>
      <div className={styles.gameInfo}>
        <div className={styles.gameImage}>
          <img src={data?.game_logo} alt={data?.game_name} />
          <div className={styles.platform}>
            <DeviceTypeIcon deviceType={data?.platform} positionTop={true}/>
          </div>
        </div>

        <div className={styles.gameDetails}>
          <h2 className={styles.title}>{data?.game_name}</h2>
          <div className={styles.columnInfo}>
            <Rating size="md" defaultValue={data?.popularity_score} />
            <div className={styles.price}>{data?.total_rewards_value_str}</div>
          </div>
        </div>
      </div>

      {/* todo: there is a css styling mix, which set here in item details Cta opacity:0 but overriden in the Cta element, need to reorganize compenent */}      {/* CTA button with a complex hidden component that opens the QR Image/ Rules and Tips/ iOS tracking modal / sending directly to store */}
      {!!data && (
        <Cta
          qrCodeComponent={qrCodeComponent}
          price={data.total_rewards_value_str}
          image={data?.game_logo}
          title={data?.game_name}
          description={data?.description}
          platform={data?.platform}
          link={data?.game_link}
        />
      )}

      {/* Rewards listing */}
      <div className={styles.rewards}>
        <div className={styles.rewardTitle}>💰 Rewards</div>
        {data?.rewards.map((reward: IRewards, index: number) => {
          const isCompleted = isRewardCompleted(reward.reward_id, data.offer_id);
          const isRecentlyCompleted = isCompleted && isRewardRecentlyCompleted(reward.reward_id, data.offer_id);
          return (
            <div 
              className={classNames(
                styles.rewardItem, 
                styles.bg,
                { 
                  [styles.completed]: isCompleted,
                  [styles.recentCompleted]: isRecentlyCompleted
                }
              )} 
              key={index}
            >
              <div className={styles.tag}>
                {reward.reward_value_str}
              </div>
              <div className={classNames(styles.text, { [styles.completedText]: isCompleted })}>
                {reward.description}
              </div>
                {isCompleted && (
                  <div className={styles.completedLabel}>
                    {isRecentlyCompleted ? "Just Completed" : "Completed"}
                  </div>
                )}
            </div>
          );
        })}
      </div>

      {/* Description section with collapsible content */}
      <div className={classNames(styles.bg)}>
        <div className={styles.rewardTitle}>Requirements</div>
        <div className={styles.smallText}>{data?.requirements}</div>
        <div className={styles.divider}></div>
        <div className={styles.rewardTitle}>Description</div>
        <div className={styles.smallText}>{data?.description}</div>
        <div className={styles.divider}></div>
      </div>
      
      {/* todo: make this a bottom CTA sticky, currently not supported */}
      {/* CTA at the bottom (possibly duplicated for better UX on scroll) */}
      {/* <div className={classNames({[styles.show]: showButton}, styles.cta)}>
        {!!data && (
          <Cta
            qrCodeImage={qrCodeImage ?? ''}
            price={data.total_rewards_value_str}
            image={data?.game_logo}
            title={data?.game_name}
            description={data?.description}
            platform={data?.platform}
            link={data?.game_link}
          />
        )}
      </div> */}
    </div>
  );
};

export default ItemDetails;
